// SequenceTemplate — design spec §2.
// 2–4 numbered steps in a horizontal flow. Connectors between.
// Reference: Stripe "How Stripe works" three-step diagram + Linear method page.

import { motion } from 'framer-motion';
import { Stage } from '../stage/Stage';
import { Eyebrow } from '../stage/Eyebrow';
import { PointableItem, DefaultOrbAnchor } from '../stage/PointableItem';
import { fadeUp, stagger } from '../motion/variants';
import type { SceneContent } from '../types';

interface SequenceProps {
  content: SceneContent;
}

export function SequenceTemplate({ content }: SequenceProps) {
  const { headline, eyebrow, items = [] } = content;
  const steps = items.slice(0, 4);
  const colsClass =
    steps.length <= 2 ? 'grid-cols-2' :
    steps.length === 3 ? 'grid-cols-3' :
    'grid-cols-4';

  return (
    <Stage>
      {/* Orb anchor — top-right, 48px disc per spec §2g. Suppressed when
          the agent is currently pointing at a step (PointableItem renders
          the orb inline at that step's position via shared layoutId). */}
      <div className="absolute top-12 right-12 max-md:top-6 max-md:right-6">
        <DefaultOrbAnchor size={48} />
      </div>

      <motion.div
        initial="hidden"
        animate="show"
        exit="exit"
        variants={stagger(0.15)}
        className="m-auto w-full max-w-[1440px] flex flex-col gap-16"
      >
        {(eyebrow || headline) && (
          <div className="text-center flex flex-col gap-6 items-center">
            {eyebrow && (
              <motion.div variants={fadeUp}>
                <Eyebrow>{eyebrow}</Eyebrow>
              </motion.div>
            )}
            {headline && (
              <motion.h2 variants={fadeUp} className="text-headline font-display max-w-[28ch] text-balance">
                {headline}
              </motion.h2>
            )}
          </div>
        )}

        <motion.ol
          variants={stagger(0.15)}
          className={`grid gap-8 ${colsClass} max-md:grid-cols-1 max-md:gap-10`}
        >
          {steps.map((s, i) => (
            <motion.li
              key={i}
              variants={fadeUp}
              className="relative flex flex-col gap-6"
            >
              <PointableItem orbTargetId={`item_${i}`} orbSize={40} orbPlacement="top-right">
                {/* Connector — horizontal hairline to next step (desktop) */}
                {i < steps.length - 1 && (
                  <span
                    aria-hidden
                    className="hidden md:block absolute top-[34px] left-[calc(100%+0.25rem)] w-[calc(2rem-0.5rem)] h-px bg-gradient-to-r from-fg-faint to-transparent"
                  />
                )}
                {/* Vertical connector for mobile */}
                {i < steps.length - 1 && (
                  <span
                    aria-hidden
                    className="md:hidden absolute left-[24px] top-[calc(100%+0.5rem)] w-px h-8 bg-gradient-to-b from-fg-faint to-transparent"
                  />
                )}

                <span className="font-mono text-accent tabular-nums leading-none text-[clamp(48px,5vw,72px)] tracking-[-0.05em] font-semibold">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <h3 className="text-headline font-display text-fg">{s.headline}</h3>
                {s.body && (
                  <p className="text-body text-fg-soft max-w-[28ch]">{s.body}</p>
                )}
              </PointableItem>
            </motion.li>
          ))}
        </motion.ol>
      </motion.div>
    </Stage>
  );
}
